import { useEffect, useId, useMemo, useState } from "react";
import { FileDown } from "lucide-react";
import { toast } from "sonner";
import JSZip from "jszip";
import { API_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  fiscalYearFromConfig,
  type PeriodPreset,
  periodOptions as sharedPeriodOptions,
  resolvePeriod,
} from "@/lib/period-presets";
import { countryConfigOrDefault, type CountryConfig } from "@/lib/country";
import {
  exportFilename,
  exportPeriodPart,
  uniqueFilename,
} from "@/lib/export-filename";

// ── Types ───────────────────────────────────────────────────────────────────

// `clientEmail` / `email` name the file when the display name has nothing a
// file name can hold (a name in another script), so exports never share a name.
export type ExportTarget =
  | {
      kind: "single";
      clientId: string;
      clientName: string;
      clientEmail?: string;
    }
  | {
      kind: "bulk";
      clients: Array<{ id: string; name: string; email?: string }>;
    };

interface ExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ExportTarget;
  /** Pre-select a period preset. When the caller is the receipts table,
   *  pass its current preset so the export dialog opens with the same
   *  window already chosen. */
  defaultPeriod?: PeriodPreset;
  /** Pre-fill the custom date-range inputs. Only honoured when
   *  `defaultPeriod === "custom"`. */
  defaultFrom?: string;
  defaultTo?: string;
  /** Whose fiscal year the presets resolve in. For a single client that is
   *  the client's own country; for a bulk export, pass the accountant's —
   *  one dialog cannot span several fiscal calendars, and their book is
   *  almost always domestic. */
  countryConfig?: CountryConfig | null;
}

// ── Component ───────────────────────────────────────────────────────────────

async function fetchExport(
  clientId: string,
  format: string,
  dateFrom: string,
  dateTo: string,
): Promise<Blob> {
  const body: Record<string, string> = { format };
  if (dateFrom) body.date_from = dateFrom;
  if (dateTo) body.date_to = dateTo;
  if (!dateFrom && !dateTo) body.period = "all";

  const res = await fetch(
    `${API_URL}/api/accountant/users/${clientId}/export`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) {
    let detail = `Export failed (${res.status})`;
    try {
      const err = await res.json();
      if (err.detail) detail = err.detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(detail);
  }
  return res.blob();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function ExportDialog({
  open,
  onOpenChange,
  target,
  defaultPeriod = "this_fy",
  defaultFrom,
  defaultTo,
  countryConfig,
}: ExportDialogProps) {
  const {
    code: countryCode,
    fiscal_year_start_month: fyStartMonth,
    fiscal_year_start_day: fyStartDay,
  } = countryConfigOrDefault(countryConfig);
  // One identity per country, not per render. Both effects below depend on
  // the fiscal year; when it was rebuilt on every render the "dialog opened"
  // effect re-ran after each state change and snapped the period and the
  // format back to their defaults, so a PDF or a different period could never
  // be exported.
  const fiscalYear = useMemo(
    () =>
      fiscalYearFromConfig({
        code: countryCode,
        fiscal_year_start_month: fyStartMonth,
        fiscal_year_start_day: fyStartDay,
      }),
    [countryCode, fyStartMonth, fyStartDay],
  );
  const periodOptions = sharedPeriodOptions(undefined, fiscalYear);
  const [period, setPeriod] = useState<PeriodPreset>(defaultPeriod);
  const [format, setFormat] = useState("excel");
  const [dateFrom, setDateFrom] = useState(defaultFrom ?? "");
  const [dateTo, setDateTo] = useState(defaultTo ?? "");
  const [isExporting, setIsExporting] = useState(false);
  const periodLabelId = useId();
  const dateFromId = useId();
  const dateToId = useId();

  const isBulk = target.kind === "bulk";
  const clientCount = isBulk ? target.clients.length : 1;

  // Sync dates when period changes
  useEffect(() => {
    if (period === "custom") return; // keep current dates
    if (period === "all") {
      setDateFrom("");
      setDateTo("");
      return;
    }
    const resolved = resolvePeriod(period, { fiscalYear });
    if (resolved.from && resolved.to) {
      setDateFrom(resolved.from);
      setDateTo(resolved.to);
    }
  }, [period, fiscalYear]);

  // Reset state when dialog opens so re-opening a cached dialog reflects
  // the latest props (e.g. the receipts page's current period).
  useEffect(() => {
    if (open) {
      setPeriod(defaultPeriod);
      setFormat("excel");
      setIsExporting(false);
      if (defaultPeriod === "custom") {
        setDateFrom(defaultFrom ?? "");
        setDateTo(defaultTo ?? "");
        return;
      }
      if (defaultPeriod === "all") {
        setDateFrom("");
        setDateTo("");
        return;
      }
      const resolved = resolvePeriod(defaultPeriod, { fiscalYear });
      setDateFrom(resolved.from ?? "");
      setDateTo(resolved.to ?? "");
    }
  }, [open, defaultPeriod, defaultFrom, defaultTo, fiscalYear]);

  const handleDateChange = (field: "from" | "to", value: string) => {
    if (field === "from") setDateFrom(value);
    else setDateTo(value);
    // Switch to custom if the user manually changes dates away from the
    // preset's canonical range.
    if (period !== "custom" && period !== "all") {
      const resolved = resolvePeriod(period, { fiscalYear });
      const changed =
        field === "from" ? value !== resolved.from : value !== resolved.to;
      if (changed) setPeriod("custom");
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    const ext = format === "pdf" ? "pdf" : "xlsx";
    const toastId = "export-dialog";

    try {
      if (isBulk) {
        toast.loading(`Exporting ${clientCount} clients...`, { id: toastId });
        const zip = new JSZip();
        const failed: string[] = [];
        const usedNames = new Set<string>();
        for (let i = 0; i < target.clients.length; i++) {
          const c = target.clients[i];
          toast.loading(`Exporting ${i + 1}/${clientCount}: ${c.name}...`, {
            id: toastId,
          });
          try {
            const blob = await fetchExport(c.id, format, dateFrom, dateTo);
            zip.file(
              uniqueFilename(
                exportFilename(
                  [c.name, c.email ?? "", c.id.slice(0, 8)],
                  dateFrom,
                  dateTo,
                  ext,
                ),
                usedNames,
              ),
              blob,
            );
          } catch {
            failed.push(c.name);
          }
        }
        const exported = clientCount - failed.length;
        if (exported === 0) {
          throw new Error(
            `Could not export any clients. ${failed.join(", ")} failed.`,
          );
        }
        toast.loading("Creating zip file...", { id: toastId });
        const zipBlob = await zip.generateAsync({ type: "blob" });
        downloadBlob(
          zipBlob,
          `taxr_exports_${exported}_clients_${exportPeriodPart(dateFrom, dateTo)}.zip`,
        );
        if (failed.length > 0) {
          toast.success(
            `Exported ${exported}/${clientCount} clients. Skipped: ${failed.join(", ")}`,
            { id: toastId },
          );
        } else {
          toast.success(`Exported ${clientCount} clients`, { id: toastId });
        }
      } else {
        toast.loading("Generating export...", { id: toastId });
        const blob = await fetchExport(
          target.clientId,
          format,
          dateFrom,
          dateTo,
        );
        downloadBlob(
          blob,
          exportFilename(
            [target.clientName, target.clientEmail ?? ""],
            dateFrom,
            dateTo,
            ext,
          ),
        );
        toast.success("Export downloaded", { id: toastId });
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Export failed", {
        id: toastId,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {isBulk ? `Export ${clientCount} clients` : "Export receipts"}
          </DialogTitle>
          <DialogDescription>
            {isBulk
              ? "Choose period and format. Exports will download as a zip file."
              : "Choose period and format to download."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Period preset */}
          <div className="space-y-1.5">
            <Label id={periodLabelId} className="text-xs">Period</Label>
            <Select
              value={period}
              onValueChange={(v) => setPeriod((v as PeriodPreset) ?? "this_fy")}
            >
              <SelectTrigger aria-labelledby={periodLabelId}>
                <SelectValue>
                  {periodOptions.find((o) => o.value === period)?.label ??
                    "Choose period"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {periodOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    <span className="flex flex-col">
                      <span>{o.label}</span>
                      {o.caption && (
                        <span className="text-[11px] text-muted-foreground">
                          {o.caption}
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {period !== "all" && period !== "custom" && (
              <p className="text-[11px] text-muted-foreground">
                {resolvePeriod(period, { fiscalYear }).caption}
              </p>
            )}
          </div>

          {/* Date range */}
          {period !== "all" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor={dateFromId} className="text-xs">From</Label>
                <Input
                  id={dateFromId}
                  type="date"
                  value={dateFrom}
                  onChange={(e) =>
                    handleDateChange(
                      "from",
                      (e.target as HTMLInputElement).value,
                    )
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={dateToId} className="text-xs">To</Label>
                <Input
                  id={dateToId}
                  type="date"
                  value={dateTo}
                  onChange={(e) =>
                    handleDateChange("to", (e.target as HTMLInputElement).value)
                  }
                />
              </div>
            </div>
          )}

          {/* Format */}
          <div className="space-y-1.5">
            <Label className="text-xs">Format</Label>
            <div className="flex gap-2">
              {(["excel", "pdf"] as const).map((f) => (
                <Button
                  key={f}
                  variant={format === f ? "default" : "outline"}
                  size="sm"
                  aria-pressed={format === f}
                  onClick={() => setFormat(f)}
                >
                  {f === "excel" ? "Excel (.xlsx)" : "PDF"}
                </Button>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isExporting}>
            <FileDown className="mr-1.5 size-4" />
            {isExporting ? "Generating..." : "Export"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
